/**
 * Chat session → PDF: render the conversation to HTML and print it via a hidden
 * iframe. Gated behind the CHAT_PDF_DOWNLOAD feature flag at the call site.
 */

import { brandColors } from "@/lib/brandColors"
import { Chat, Message as ChatMessage } from "@/types/assistantChat"
import { Marked } from "marked"
// Imported as a string: the print iframe has no Tailwind / app CSS.
import baseCss from "./chatPdf.css?raw"
import { extractUniqueCitations } from "./chatUtils"

// Colours — brand tokens from the shared palette; chrome neutrals are
// export-specific. Brand colours reach chatPdf.css via these `--c-*` vars.

/** Brand colours surfaced to chatPdf.css as `--c-*` custom properties. */
const CSS_VARS: Record<string, string> = {
  "--c-brand-yellow": brandColors.yellow1,
  "--c-band-bg": brandColors.grey2,
  "--c-white": brandColors.white,
  "--c-footer-logo": brandColors.grey2,
}

// Footer @page-box neutrals (separator hairline, page-number text).
const FOOTER_RULE = "#d4d4d8"
const FOOTER_TEXT = "#a1a1aa"

/** Serialize CSS_VARS into a `:root { … }` block. */
function buildRootVars(): string {
  const decls = Object.entries(CSS_VARS)
    .map(([name, value]) => `${name}: ${value};`)
    .join(" ")
  return `:root { ${decls} }`
}

/** Escape a string for safe interpolation into HTML. */
function esc(value: string | null | undefined): string {
  return (value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

// Local Marked instance — escapes raw HTML blocks via esc() instead of passing
// them through. A local instance avoids mutating the global `marked`.
const _marked = new Marked({
  gfm: true,
  breaks: false,
  renderer: {
    html({ text }: { text: string }): string {
      return esc(text)
    },
  },
})

/** Render GFM markdown to HTML with raw HTML passthrough disabled. */
function renderMarkdown(content: string): string {
  return _marked.parse(content) as string
}

/** Fallback logo mark if the live wordmark isn't in the DOM. */
const LOGO_MARK_SVG = `<svg viewBox="0 0 81 60" fill="none" xmlns="http://www.w3.org/2000/svg" class="footer-logo" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M37.8494 12.3949C32.0116 15.9084 28.1066 22.3063 28.1066 29.6163C28.1066 36.9266 32.012 43.3247 37.8502 46.8382C35.5908 47.8051 33.1027 48.3404 30.4896 48.3404C20.1488 48.3404 11.7659 39.9575 11.7659 29.6167C11.7659 19.2758 20.1488 10.8929 30.4896 10.8929C33.1024 10.8929 35.5902 11.4281 37.8494 12.3949ZM52.7184 49.1896C61.6314 47.1368 68.2775 39.1527 68.2775 29.6163C68.2775 20.0796 61.631 12.0952 52.7175 10.0427C50.6691 7.7184 48.2619 5.71791 45.5825 4.1279C47.0672 3.87521 48.5947 3.74343 50.1544 3.74343C64.8878 3.74343 76.788 15.5034 76.788 29.9566C76.788 44.4099 64.8878 56.1698 50.1544 56.1698C48.2787 56.1698 46.4495 55.9793 44.6852 55.6169C47.7264 53.953 50.4437 51.771 52.7184 49.1896ZM30.4896 59.2342C33.7284 59.2342 36.8458 58.7143 39.7628 57.7535C43.0032 58.9316 46.5045 59.5742 50.1544 59.5742C66.7198 59.5742 80.1923 46.3378 80.1923 29.9566C80.1923 13.5754 66.7198 0.339112 50.1544 0.339112C46.8701 0.339112 43.7059 0.859434 40.7436 1.82224C37.5485 0.643099 34.0943 -0.000854492 30.4896 -0.000854492C14.1323 -0.000854492 0.87207 13.2594 0.87207 29.6167C0.87207 45.974 14.1323 59.2342 30.4896 59.2342Z" fill="currentColor"/></svg>`

/** Genuine turnus.ai logo from the live DOM: wordmark, then mark, then fallback. */
function findLogoSvgMarkup(): string {
  const wordmark = document.querySelector('svg[viewBox="0 0 348 60"]')
  if (wordmark) {
    return wordmark.outerHTML
  }
  return findLogoMarkSvg()
}

/** Circular turnus.ai mark (LogoMark) for the assistant avatar, like the chat. */
function findLogoMarkSvg(): string {
  const mark = document.querySelector('svg[viewBox="0 0 81 60"]')
  return mark ? mark.outerHTML : LOGO_MARK_SVG
}

/**
 * Wordmark as a base64 SVG data-URL — required by the `@page` footer margin box,
 * which only accepts `content: url(...)` (no inline SVG / currentColor). Bakes
 * in an explicit colour and a width from the viewBox aspect ratio.
 */
function buildLogoDataUrl(color: string, heightPx = 12): string {
  let svg = findLogoSvgMarkup()

  // Width from viewBox aspect ratio (wordmark 348x60, mark 81x60).
  const viewBoxMatch = svg.match(
    /viewBox="0 0 (\d+(?:\.\d+)?) (\d+(?:\.\d+)?)"/,
  )
  const ratio =
    viewBoxMatch && Number(viewBoxMatch[2]) > 0
      ? Number(viewBoxMatch[1]) / Number(viewBoxMatch[2])
      : 348 / 60
  const widthPx = Math.round(heightPx * ratio)

  svg = svg
    .replace(/\swidth="[^"]*"/g, "")
    .replace(/\sheight="[^"]*"/g, "")
    .replace(/\sclass="[^"]*"/g, "")
    .replace(/currentColor/g, color)
    .replace(/<svg /, `<svg width="${widthPx}" height="${heightPx}" `)

  if (!svg.includes("xmlns=")) {
    svg = svg.replace(/<svg /, '<svg xmlns="http://www.w3.org/2000/svg" ')
  }

  return `data:image/svg+xml;base64,${btoa(svg)}`
}

/**
 * Per-render print footer via `@page` margin boxes (logo bottom-left, page
 * number bottom-right, hairline above). Repeats on every page; in TS rather
 * than the .css because its `content` is dynamic (label + logo data-URL).
 */
function buildFooterCss(pageLabel: string): string {
  const logoDataUrl = buildLogoDataUrl(CSS_VARS["--c-footer-logo"], 12)
  return `@media print {
    @page {
      @bottom-left {
        content: url("${logoDataUrl}");
        vertical-align: middle;
        border-top: 1px solid ${FOOTER_RULE}; padding-top: 1mm;
      }
      @bottom-center {
        content: "";
        border-top: 1px solid ${FOOTER_RULE};
      }
      @bottom-right {
        content: "${esc(pageLabel)} " counter(page) " / " counter(pages);
        font-size: 8pt; color: ${FOOTER_TEXT};
        vertical-align: middle;
        border-top: 1px solid ${FOOTER_RULE}; padding-top: 1mm;
      }
    }
  }`
}

/** Dark branded header band: logo + date, then the chat title. */
function renderHeader(logo: string, title: string, dateLabel: string): string {
  return `<header class="doc-header">
    <div class="doc-brandband">
      <div class="doc-brandrow">
        <span class="header-logo">${logo}</span>
        <p class="doc-meta">${esc(dateLabel)}</p>
      </div>
      <h1 class="doc-title">${esc(title)}</h1>
    </div>
  </header>`
}

/** Per-message sources: filename + page number. */
function renderCitations(
  message: ChatMessage,
  sourcesLabel: string,
  pageLabel: string,
  unknownSourceLabel: string,
): string {
  const citations = extractUniqueCitations(
    message,
    message.role,
    unknownSourceLabel,
  )
  if (citations.length === 0) {
    return ""
  }
  const items = citations
    .map((c) => {
      const name = esc(c.sourceFileName)
      const page = c.sourceFilePage
        ? ` <span class="source-page">${esc(pageLabel)} ${esc(String(c.sourceFilePage))}</span>`
        : ""
      return `<p class="source-item">• ${name}${page}</p>`
    })
    .join("")
  return `<div class="sources">
    <p class="sources-label">${esc(sourcesLabel)}</p>
    ${items}
  </div>`
}

/** A single message bubble (+ avatar for assistant) and its sources. */
function renderMessage(
  message: ChatMessage,
  avatarMark: string,
  sourcesLabel: string,
  pageLabel: string,
  userLabel: string,
  assistantLabel: string,
  unknownSourceLabel: string,
): string {
  const isUser = message.role === "user"
  const avatar = isUser ? "" : `<div class="avatar">${avatarMark}</div>`
  const bodyHtml = renderMarkdown(message.content)
  const sourcesHtml = renderCitations(
    message,
    sourcesLabel,
    pageLabel,
    unknownSourceLabel,
  )

  return `<div class="msg-group">
    <div class="msg msg-${message.role}">
      ${avatar}
      <div class="bubble bubble-${message.role}">
        <p class="role-label">${esc(isUser ? userLabel : assistantLabel)}</p>
        ${bodyHtml}
      </div>
    </div>
    ${sourcesHtml}
  </div>`
}

interface RenderOptions {
  title: string
  dateLabel: string
  sourcesLabel: string
  pageLabel: string
  userLabel: string
  assistantLabel: string
  unknownSourceLabel: string
  lang: string
}

/** Build the self-contained HTML document for the chat session. */
export function renderChatSessionHtml(
  chat: Chat,
  {
    title,
    dateLabel,
    sourcesLabel,
    pageLabel,
    userLabel,
    assistantLabel,
    unknownSourceLabel,
    lang,
  }: RenderOptions,
): string {
  const logo = findLogoSvgMarkup() // wordmark, for header/footer
  const avatarMark = findLogoMarkSvg() // circular mark, for assistant avatar

  const rows = (chat.messages ?? [])
    .filter((m) => m.role === "user" || m.role === "assistant")
    .map((m) =>
      renderMessage(
        m,
        avatarMark,
        sourcesLabel,
        pageLabel,
        userLabel,
        assistantLabel,
        unknownSourceLabel,
      ),
    )
    .join("\n")

  const body = `
    <div class="doc">
      ${renderHeader(logo, title, dateLabel)}
      ${rows}
    </div>
    <div class="screen-footer"><span class="footer-logo">${logo}</span></div>`

  return `<!DOCTYPE html>
<html lang="${esc(lang)}">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>${esc(title)}</title>
<style>${buildRootVars()}</style>
<style>${baseCss}</style>
<style>${buildFooterCss(pageLabel)}</style>
</head>
<body>${body}</body>
</html>`
}

/** Print the rendered HTML via an off-screen iframe (opens the Save-as-PDF dialog). */
export function exportChatAsPdf(html: string): void {
  const iframe = document.createElement("iframe")
  iframe.setAttribute("aria-hidden", "true")
  iframe.setAttribute("sandbox", "allow-modals allow-same-origin")
  iframe.style.position = "fixed"
  iframe.style.right = "0"
  iframe.style.bottom = "0"
  iframe.style.width = "0"
  iframe.style.height = "0"
  iframe.style.border = "0"

  const cleanup = () => {
    // Defer so the print job can read the document first.
    window.setTimeout(() => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe)
      }
    }, 1000)
  }

  iframe.onload = () => {
    const frameWindow = iframe.contentWindow
    if (!frameWindow) {
      cleanup()
      return
    }
    frameWindow.addEventListener("afterprint", cleanup)
    frameWindow.focus()
    frameWindow.print()
    // Fallback if afterprint never fires (e.g. dialog dismissed).
    cleanup()
  }

  iframe.srcdoc = html
  document.body.appendChild(iframe)
}
